import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Plus, X, Zap, Save, Loader2, Layers } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../contexts/AuthContext";
import { opts, fmt } from "./constants";

const FASES = ["R", "S", "T", "RST"];
const NEW_CIRCUITO = () => ({
  numero: "", luces_n: 0, luces_w: 20, lum2_n: 0, lum2_w: 0,
  tomas_simples_n: 0, tomas_simples_w: 100,
  tomas_esp_descripcion: "", tomas_esp_n: 0, tomas_esp_w: 0,
  otras_descripcion: "", otras_n: 0, otras_w: 0,
  fase: "R", factor_arranque: 1.0,
});
const NEW_TABLERO = (n) => ({
  nombre: `TS-D${String.fromCharCode(64 + n)}`,
  descripcion: "",
  circuitos: [{ ...NEW_CIRCUITO(), numero: "1" }],
});

export function TablerosTab({ budgetId }) {
  const [tableros, setTableros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const fetchTableros = useCallback(async () => {
    if (!budgetId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/budgets/${budgetId}/tableros`, opts);
      setTableros(res.data.tableros || []);
    } catch {
      setTableros([]);
    } finally {
      setLoading(false);
    }
  }, [budgetId]);

  useEffect(() => { fetchTableros(); }, [fetchTableros]);

  // Auto-recalc preview cuando cambian los datos
  const recalcPreview = useCallback(async (next) => {
    if (!budgetId) return;
    try {
      const res = await axios.post(`${API}/budgets/${budgetId}/tableros/calc-preview`, { tableros: next }, opts);
      setTableros(res.data.tableros || []);
    } catch (err) {
      console.error("Error en preview:", err);
    }
  }, [budgetId]);

  const addTablero = () => {
    const next = [...tableros, NEW_TABLERO(tableros.length + 1)];
    setTableros(next);
    setActiveTab(next.length - 1);
    recalcPreview(next);
  };

  const removeTablero = (idx) => {
    if (!confirm("¿Eliminar este tablero seccional?")) return;
    const next = tableros.filter((_, i) => i !== idx);
    setTableros(next);
    setActiveTab(Math.max(0, activeTab - 1));
    recalcPreview(next);
  };

  const updateTablero = (idx, patch) => {
    const next = tableros.map((t, i) => i === idx ? { ...t, ...patch } : t);
    setTableros(next);
  };

  const updateCircuito = (tIdx, cIdx, field, value) => {
    const next = tableros.map((t, i) => {
      if (i !== tIdx) return t;
      const circs = t.circuitos.map((c, j) => j === cIdx ? { ...c, [field]: value } : c);
      return { ...t, circuitos: circs };
    });
    setTableros(next);
  };

  const addCircuito = (tIdx) => {
    const next = tableros.map((t, i) => {
      if (i !== tIdx) return t;
      const newC = { ...NEW_CIRCUITO(), numero: String(t.circuitos.length + 1) };
      return { ...t, circuitos: [...t.circuitos, newC] };
    });
    setTableros(next);
    recalcPreview(next);
  };

  const removeCircuito = (tIdx, cIdx) => {
    const next = tableros.map((t, i) => {
      if (i !== tIdx) return t;
      return { ...t, circuitos: t.circuitos.filter((_, j) => j !== cIdx) };
    });
    setTableros(next);
    recalcPreview(next);
  };

  const handleBlur = () => recalcPreview(tableros);

  const handleSave = async () => {
    if (!budgetId) {
      toast.error("Guardá el presupuesto primero");
      return;
    }
    setSaving(true);
    try {
      // Limpiar campos calculados antes de enviar
      const clean = tableros.map(t => ({
        nombre: t.nombre, descripcion: t.descripcion || "",
        circuitos: (t.circuitos || []).map(c => ({
          numero: c.numero || "",
          luces_n: Number(c.luces_n) || 0, luces_w: Number(c.luces_w) || 0,
          lum2_n: Number(c.lum2_n) || 0, lum2_w: Number(c.lum2_w) || 0,
          tomas_simples_n: Number(c.tomas_simples_n) || 0,
          tomas_simples_w: Number(c.tomas_simples_w) || 0,
          tomas_esp_descripcion: c.tomas_esp_descripcion || "",
          tomas_esp_n: Number(c.tomas_esp_n) || 0,
          tomas_esp_w: Number(c.tomas_esp_w) || 0,
          otras_descripcion: c.otras_descripcion || "",
          otras_n: Number(c.otras_n) || 0,
          otras_w: Number(c.otras_w) || 0,
          fase: c.fase || "R",
          factor_arranque: Number(c.factor_arranque) || 1.0,
        })),
      }));
      const res = await axios.put(`${API}/budgets/${budgetId}/tableros`, { tableros: clean }, opts);
      setTableros(res.data.tableros || []);
      toast.success(`${res.data.count} tablero(s) guardado(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const totalGeneral = useMemo(() => {
    return tableros.reduce((sum, t) => sum + (t.potencia_total_w || 0), 0);
  }, [tableros]);

  if (!budgetId) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50 p-6">
        <div className="text-center max-w-md">
          <Zap size={36} className="mx-auto text-gray-300 mb-4" />
          <p className="text-sm text-gray-600 font-semibold">Análisis de Cargas por Tablero Seccional</p>
          <p className="text-xs text-gray-500 mt-2">Guardá el presupuesto primero para poder definir los tableros eléctricos.</p>
        </div>
      </div>
    );
  }

  const t = tableros[activeTab];

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="px-4 py-3 bg-white border-b border-gray-100 flex items-center gap-3 flex-shrink-0">
        <Layers size={16} className="text-[#2E6DA4]" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[#1A4B7A]">Análisis de Cargas — Tableros Seccionales</p>
          <p className="text-[10px] text-gray-500">Diseño eléctrico. Cálculo automático de potencia, corriente, fase, TM y conductor por circuito.</p>
        </div>
        <div className="text-right">
          <p className="text-[9px] text-gray-400 uppercase tracking-wider">Potencia Total</p>
          <p className="text-sm font-bold text-emerald-700 tabular-nums">{fmt(Math.round(totalGeneral))} W</p>
        </div>
        <button onClick={addTablero}
          className="flex items-center gap-1.5 text-xs font-semibold text-[#2E6DA4] bg-[#EBF3FB] hover:bg-[#D9EAF8] px-3 py-1.5 rounded-lg transition-colors"
          data-testid="add-tablero-btn">
          <Plus size={12} /> Nuevo Tablero
        </button>
        <button onClick={handleSave} disabled={saving || tableros.length === 0}
          className="flex items-center gap-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
          data-testid="save-tableros-btn">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          {saving ? "Guardando..." : "Guardar"}
        </button>
      </div>

      {tableros.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-gray-400 p-12 text-center">
          <div className="max-w-md">
            <Zap size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm font-semibold">No hay tableros seccionales aún</p>
            <p className="text-xs mt-1">Hacé clic en "Nuevo Tablero" para comenzar el análisis.</p>
          </div>
        </div>
      ) : (
        <>
          {/* Pestañas de tableros */}
          <div className="flex border-b border-gray-200 bg-white px-3 overflow-x-auto flex-shrink-0">
            {tableros.map((tab, i) => (
              <button key={i} onClick={() => setActiveTab(i)}
                className={`flex items-center gap-2 px-3 py-2 text-xs font-semibold whitespace-nowrap border-b-2 ${activeTab === i ? "border-[#2E6DA4] text-[#2E6DA4]" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
                <span>{tab.nombre || `Tablero ${i + 1}`}</span>
                <span className="text-[9px] text-gray-400">({tab.num_circuitos || 0} cto)</span>
                <X size={11} className="hover:text-red-500"
                  onClick={(e) => { e.stopPropagation(); removeTablero(i); }} />
              </button>
            ))}
          </div>

          {/* Datos del tablero activo */}
          {t && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 px-4 py-3 bg-white border-b border-gray-100 flex-shrink-0">
                <input value={t.nombre} onChange={e => updateTablero(activeTab, { nombre: e.target.value })} onBlur={handleBlur}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-bold text-[#1A4B7A] focus:ring-2 focus:ring-[#2E6DA4]/30"
                  placeholder="TS-DA" data-testid={`tablero-nombre-${activeTab}`} />
                <input value={t.descripcion || ""} onChange={e => updateTablero(activeTab, { descripcion: e.target.value })} onBlur={handleBlur}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30 md:col-span-2"
                  placeholder="Descripción del tablero (ej: Tablero Seccional Departamento A)" />
              </div>

              {/* KPIs del tablero */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 px-4 py-2 bg-[#EBF3FB] border-b border-[#C5DCF0] flex-shrink-0 text-center">
                <Kpi label="Potencia R" val={`${fmt(Math.round(t.potencia_r_w || 0))} W`} color="text-purple-700" />
                <Kpi label="Potencia S" val={`${fmt(Math.round(t.potencia_s_w || 0))} W`} color="text-orange-700" />
                <Kpi label="Potencia T" val={`${fmt(Math.round(t.potencia_t_w || 0))} W`} color="text-blue-700" />
                <Kpi label="Corriente Princ." val={`${fmt(t.corriente_principal_a || 0)} A`} color="text-emerald-700" />
                <Kpi label="TM Principal" val={t.tm_principal || "—"} color="text-[#1A4B7A]" />
              </div>

              {/* Tabla de circuitos */}
              <div className="flex-1 overflow-auto">
                <table className="w-full text-xs" style={{ minWidth: 1400 }}>
                  <thead className="bg-[#2E6DA4] text-white sticky top-0 z-10">
                    <tr>
                      {["Cto", "Luces N°", "Luces W", "Lum2 N°", "Lum2 W",
                        "Tomas N°", "Tomas W", "Especial Desc.", "Esp N°", "Esp W",
                        "Otras Desc.", "Otras N°", "Otras W", "Fase", "Fact.",
                        "Potencia (W)", "Corr. (A)", "TM", "Conductor", ""].map((h, i) => (
                          <th key={i} className="px-1.5 py-1.5 text-center font-semibold text-[10px]">{h}</th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(t.circuitos || []).map((c, ci) => (
                      <tr key={ci} className="border-b border-gray-100 hover:bg-gray-50/50">
                        <td className="px-1 py-1 w-12">
                          <input value={c.numero} onChange={e => updateCircuito(activeTab, ci, "numero", e.target.value)} onBlur={handleBlur}
                            className="w-full border border-gray-200 rounded px-1 py-0.5 text-[10px] text-center" placeholder="#1" />
                        </td>
                        {[
                          ["luces_n", "number"], ["luces_w", "number"],
                          ["lum2_n", "number"], ["lum2_w", "number"],
                          ["tomas_simples_n", "number"], ["tomas_simples_w", "number"],
                          ["tomas_esp_descripcion", "text"], ["tomas_esp_n", "number"], ["tomas_esp_w", "number"],
                          ["otras_descripcion", "text"], ["otras_n", "number"], ["otras_w", "number"],
                        ].map(([field, type], k) => (
                          <td key={k} className="px-0.5 py-1">
                            <input
                              type={type}
                              value={c[field] || (type === "number" ? 0 : "")}
                              onChange={e => updateCircuito(activeTab, ci, field, type === "number" ? (parseFloat(e.target.value) || 0) : e.target.value)}
                              onBlur={handleBlur}
                              className="w-full border border-gray-200 rounded px-1 py-0.5 text-[10px] text-right focus:ring-1 focus:ring-[#2E6DA4]"
                              data-testid={`circ-${activeTab}-${ci}-${field}`}
                            />
                          </td>
                        ))}
                        <td className="px-0.5 py-1 w-14">
                          <select value={c.fase || "R"} onChange={e => updateCircuito(activeTab, ci, "fase", e.target.value)} onBlur={handleBlur}
                            className="w-full border border-gray-200 rounded px-0.5 py-0.5 text-[10px] bg-white">
                            {FASES.map(f => <option key={f}>{f}</option>)}
                          </select>
                        </td>
                        <td className="px-0.5 py-1 w-14">
                          <input type="number" step="0.1" value={c.factor_arranque || 1.0} onChange={e => updateCircuito(activeTab, ci, "factor_arranque", parseFloat(e.target.value) || 1.0)} onBlur={handleBlur}
                            className="w-full border border-gray-200 rounded px-1 py-0.5 text-[10px] text-right" />
                        </td>
                        {/* Calculados (read-only) */}
                        <td className="px-1.5 py-1 text-right text-[10px] font-bold text-emerald-700 tabular-nums">{fmt(Math.round(c.potencia_w || 0))}</td>
                        <td className="px-1.5 py-1 text-right text-[10px] font-bold text-blue-700 tabular-nums">{(c.corriente_a || 0).toFixed(2)}</td>
                        <td className="px-1.5 py-1 text-right text-[10px] font-semibold text-[#1A4B7A]">{c.tm_sugerida || "—"}</td>
                        <td className="px-1.5 py-1 text-right text-[10px] text-gray-700">{c.conductor_sugerido || "—"}</td>
                        <td className="px-1 py-1 w-8 text-center">
                          <button onClick={() => removeCircuito(activeTab, ci)} className="text-red-400 hover:text-red-600">
                            <X size={11} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="px-4 py-2 border-t border-gray-200 bg-white flex-shrink-0">
                <button onClick={() => addCircuito(activeTab)}
                  className="text-xs font-semibold text-[#2E6DA4] hover:bg-[#EBF3FB] px-3 py-1 rounded-lg flex items-center gap-1.5"
                  data-testid="add-circuito-btn">
                  <Plus size={12} /> Agregar Circuito
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ label, val, color }) {
  return (
    <div>
      <p className="text-[9px] text-gray-500 uppercase tracking-wider">{label}</p>
      <p className={`text-xs font-bold tabular-nums ${color}`}>{val}</p>
    </div>
  );
}
